// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {actionsRolesAllow} from './ActionsRolesAllow';
import type {RoleDescriptionProps} from './RoleDescription';

const developerRoleMessages = defineMessages({
  roleName: {
    id: 'docsApp.components.contrib.roleDescriptions.developerRole.roleName',
    description: 'The name of the developer role.',
    defaultMessage: 'Developer',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.developerRole.shortDescription',
    description: 'The short description of the developer role.',
    defaultMessage: 'Can view secret key, create api keys, and edit settings',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.developerRole.descriptionOfWhatRoleCanDo',
    description: 'The description of what the developer role can do.',
    defaultMessage:
      'This role is for developers who need to set up a Stripe integration. This role has access to the secret key, which grants access to almost all API resources.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.developerRole.descriptionOfWhatRoleCannotDo',
    description: "The description of what the developer role can't do.",
    defaultMessage:
      "They can't add or edit bank accounts, invite team members, or change the account owner.",
  },
});

const developerRoleDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...developerRoleMessages.roleName} />,
  roleExternalId: 'developer',
  isBeta: false,
  shortDescription: (
    <FormattedMessage {...developerRoleMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage {...developerRoleMessages.descriptionOfWhatRoleCanDo} />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage
      {...developerRoleMessages.descriptionOfWhatRoleCannotDo}
    />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputes} />,
    <FormattedMessage {...actionsRolesAllow.createViewRefundPayments} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteProducts} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteCustomers} />,
    <FormattedMessage
      {...actionsRolesAllow.createViewEditRejectConnectedAccounts}
    />,
    <FormattedMessage {...actionsRolesAllow.viewBalance} />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage
      {...actionsRolesAllow.viewAndEditAlmostAllProductSettingsExceptTerminalAndClimate}
    />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
    <FormattedMessage {...actionsRolesAllow.viewPayouts} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.editConnectedAcctCapabilities} />,
    <FormattedMessage
      {...actionsRolesAllow.editConnectedAccountPayoutSchedule}
    />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage {...actionsRolesAllow.editTerminalAndClimateSettings} />,
  ],
};

export const allDeveloperRoleProps = [developerRoleDescriptionProps];
